@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.entrys

import org.example.project.manager.NodeScope

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.dashboard.ScreenManager
import org.example.project.dashboard.DashboardNode
import org.example.project.dashboard.DashboardScreen
import org.example.project.dashboard.MobileDashboardScreen
import org.example.project.manager.LumaticaApiSettings
import org.example.project.login.DesktopLoginScreen
import org.example.project.login.MobileLoginScreen
import org.example.project.login.PasswordResetScreen
import org.example.project.enginepersonal.StempeluhrScreen
import org.example.project.registrierung.ZweiFaktorRecoveryBereich
import org.example.project.registrierung.ZweiFaktorRecoveryScreen
import org.example.project.setup.SetupApi
import org.example.project.setup.SYSlumaticasetup
import org.example.project.service.LoginService
import org.example.project.service.GlobalManager
import org.example.project.service.MobileAnsicht
import org.example.project.service.MobileAnsichtenApi
import org.example.project.service.RollenService
import org.example.project.service.ScreenConfigApi
import org.example.project.service.ScreenEnvironmentApi
import org.example.project.service.SystemPushService
import org.example.project.service.shouldForceLogoutAfterBuildChange
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event

private fun currentSysPath(): String =
    when (val path = window.location.pathname.trimEnd('/').lowercase().ifBlank { "/" }) {
        "/", "/login" -> "/"
        "/mobile", "/companion", "/setup", "/stempeluhr", "/passwort-reset", "/zwei-faktor-reset" -> path
        else -> ""
    }

private fun navigateSysTo(path: String) {
    val target = if (path.startsWith("/")) path else "/$path"
    if (currentSysPath() == target) return
    window.history.pushState(null, "", target)
    window.dispatchEvent(Event("popstate"))
}

@Composable
fun SYSEntry() {
    LumaticaApiSettings.nodeIp = window.location.hostname
    LumaticaApiSettings.nodePort = window.location.port

    val companion = window.location.pathname.trimEnd('/').lowercase() == "/companion"
    var path by remember { mutableStateOf(currentSysPath()) }
    var screen by remember {
        mutableStateOf(
            when (currentSysPath()) {
                "/" -> "LOGIN"
                "/mobile", "/companion" -> "MOBILE"
                else -> "NONE"
            }
        )
    }
    var sessionReady by remember { mutableStateOf(false) }
    var mobileAnsichten by remember { mutableStateOf<List<MobileAnsicht>>(emptyList()) }
    var setupLoaded by remember { mutableStateOf(false) }
    var setupDone by remember { mutableStateOf(false) }
    var setupLoadError by remember { mutableStateOf(false) }
    var mobileAuthRevision by remember { mutableStateOf(0) }
    val screenConfigVersion = ScreenConfigApi.version()
    val scope = rememberCoroutineScope()
    if (!LoginService.isLoggedIn()) {
        org.example.project.login.PublicWebsiteFooter(
            compact = path in setOf("/mobile", "/companion", "/driver", "/driver/register")
        )
    }

    LaunchedEffect(Unit) {
        if (window.location.pathname.trimEnd('/').lowercase() == "/login") {
            window.history.replaceState(null, "", "/")
        }
    }

    suspend fun initializeLoggedInSession(): Boolean {
        SystemPushService.ensureStarted()
        RollenService.refreshCurrentUserRole(NodeScope.SYS)
        ScreenManager.applyScreenConfig(ScreenConfigApi.loadScreenConfig())
        ScreenManager.applyScreenEnvironments(ScreenEnvironmentApi.all(), ScreenEnvironmentApi.environment())
        mobileAnsichten = MobileAnsichtenApi.available()
        return true
    }

    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = { path = currentSysPath() }
        window.addEventListener("popstate", listener)
        onDispose { window.removeEventListener("popstate", listener) }
    }

    LaunchedEffect(path) {
        screen = when (path) {
            "/" -> if (LoginService.isLoggedIn() && LoginService.getUser()?.zugangDesktop == true) "DASHBOARD" else "LOGIN"
            "/mobile", "/companion" -> "MOBILE"
            "/setup" -> "SETUP"
            else -> "NONE"
        }
    }

    LaunchedEffect(Unit) {
        val setupResult = runCatching { SetupApi.loadCusNodeSettings()?.nodeEingerichtet ?: false }
        setupLoadError = setupResult.isFailure
        setupDone = setupResult.getOrDefault(true)
        setupLoaded = true
        if (setupLoadError) {
            sessionReady = true
            screen = if (path == "/") "LOGIN" else "NONE"
            return@LaunchedEffect
        }
        if (!setupDone) {
            LoginService.clearLocalLogin()
            sessionReady = true
            screen = "SETUP"
            return@LaunchedEffect
        }

        LoginService.restoreSession()
        if (path == "/mobile") {
            if (LoginService.isLoggedIn()) LoginService.logout()
            sessionReady = true
            screen = if ((path == "/mobile" || path == "/companion")) "MOBILE" else "LOGIN"
            return@LaunchedEffect
        }
        if (!companion && shouldForceLogoutAfterBuildChange("SYS")) {
            LoginService.logout()
            sessionReady = true
            screen = when (path) {
                "/" -> "LOGIN"
                "/mobile", "/companion" -> "MOBILE"
                else -> "NONE"
            }
            return@LaunchedEffect
        }

        if (!LoginService.isLoggedIn()) {
            sessionReady = true
            screen = when (path) {
                "/" -> "LOGIN"
                "/mobile", "/companion" -> "MOBILE"
                else -> "NONE"
            }
            return@LaunchedEffect
        }

        sessionReady = false
        val initialized = runCatching { initializeLoggedInSession() }.getOrElse { false }
        if (initialized) {
            sessionReady = true
            screen = when (path) {
                "/" -> if (LoginService.getUser()?.zugangDesktop == true) "DASHBOARD" else {
                    LoginService.logout()
                    "LOGIN"
                }
                "/mobile", "/companion" -> "MOBILE"
                else -> "NONE"
            }
            return@LaunchedEffect
        }
        LoginService.logout()
        sessionReady = true
        screen = when (path) {
            "/" -> "LOGIN"
            "/mobile", "/companion" -> "MOBILE"
            else -> "NONE"
        }
    }

    LaunchedEffect(
        sessionReady,
        GlobalManager.jsonDomainVersion("personal"),
        GlobalManager.jsonTopicVersion("mobile-ansichten.json")
    ) {
        if (sessionReady && LoginService.isLoggedIn()) {
            mobileAnsichten = MobileAnsichtenApi.available()
        }
    }

    LaunchedEffect(screenConfigVersion) {
        if (!LoginService.isLoggedIn()) return@LaunchedEffect
        runCatching { ScreenManager.applyScreenConfig(ScreenConfigApi.loadScreenConfig()) }
        runCatching { ScreenManager.applyScreenEnvironments(ScreenEnvironmentApi.all(), ScreenEnvironmentApi.environment()) }
    }

    if (!setupLoaded) {
        org.example.project.utils.DashboardVerlaufHintergrund()
        return
    }
    if (path == "/zwei-faktor-reset") {
        ZweiFaktorRecoveryScreen(ZweiFaktorRecoveryBereich.SYS) {
            navigateSysTo("/")
            screen = "LOGIN"
        }
        return
    }
    if (path == "/passwort-reset") {
        PasswordResetScreen {
            navigateSysTo("/")
            screen = "LOGIN"
        }
        return
    }
    if (setupLoadError) {
        org.example.project.utils.DashboardVerlaufHintergrund()
        Div { Text("SYS-Verbindung konnte nicht hergestellt werden.") }
        return
    }
    if (!sessionReady) {
        org.example.project.utils.DashboardVerlaufHintergrund()
        return
    }
    if ((path == "/mobile" || path == "/companion")) {
        @Suppress("UNUSED_EXPRESSION")
        mobileAuthRevision
        if (LoginService.isLoggedIn() && (if (companion) LoginService.getUser()?.zugangCompanion == true else LoginService.getUser()?.zugangMobil == true)) {
            MobileDashboardScreen(
                art = NodeScope.SYS,
                screens = ScreenManager.mobileDashboardScreens(DashboardNode.SYS_MOBILE)
                    .filter { MobileAnsichtenApi.active(mobileAnsichten, it.key, companion = companion) },
                onLogout = {
                    if (companion) scope.launch { org.example.project.service.CompanionBridgeService.disconnect() }
                    else mobileAuthRevision += 1
                }
            )
        } else {
            if (LoginService.isLoggedIn()) LoginService.logout()
            if (companion) {
                LaunchedEffect(Unit) { org.example.project.service.CompanionBridgeService.disconnect() }
                return
            }
            MobileLoginScreen(art = NodeScope.SYS) { _, _ ->
                scope.launch {
                    val initialized = runCatching { initializeLoggedInSession() }.getOrElse { false }
                    if (!initialized) {
                        LoginService.logout()
                        return@launch
                    }
                    mobileAuthRevision += 1
                }
            }
        }
        return
    }
    if (path == "/stempeluhr") {
        StempeluhrScreen()
        return
    }
    when (screen) {
        "SETUP" -> {
            if (setupDone) {
                screen = if (LoginService.isLoggedIn()) "DASHBOARD" else "LOGIN"
            } else {
                SYSlumaticasetup {
                    scope.launch {
                        setupDone = true
                        LoginService.clearLocalLogin()
                        screen = "LOGIN"
                        navigateSysTo("/")
                    }
                }
            }
        }
        "LOGIN" -> DesktopLoginScreen(art = NodeScope.SYS) { _, _ ->
            scope.launch {
                val initialized = runCatching { initializeLoggedInSession() }.getOrElse { false }
                if (!initialized) {
                    LoginService.logout()
                    screen = "LOGIN"
                    return@launch
                }
                screen = "DASHBOARD"
            }
        }
        "DASHBOARD" ->
            if (!LoginService.isLoggedIn() || LoginService.getUser()?.zugangDesktop != true) {
                if (LoginService.isLoggedIn()) LoginService.logout()
                screen = "LOGIN"
                DesktopLoginScreen(art = NodeScope.SYS) { _, _ ->
                    scope.launch {
                        val initialized = runCatching { initializeLoggedInSession() }.getOrElse { false }
                        if (!initialized) {
                            LoginService.logout()
                            screen = "LOGIN"
                            return@launch
                        }
                        screen = "DASHBOARD"
                    }
                }
            } else {
                DashboardScreen(
                    node = DashboardNode.SYS,
                    onLogout = {
                        LoginService.logout()
                        screen = "LOGIN"
                        navigateSysTo("/")
                    }
                )
            }
        else -> org.example.project.utils.DashboardVerlaufHintergrund()
    }
}
